import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { WavRenderer } from '../utils/wav_renderer';

import { X } from 'react-feather';

// Added Additionally
import WebCam from '../components/WebCam';
import { Modal, Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import { Settings, AlertOctagon } from 'lucide-react';

/**
 * Type for all event logs
 */
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

export function Interview() {
  //add
  const [instructions, setInstructions] = useState<string>('');
  const [resumeContent, setResumeContent] = useState<string>('');
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const audioQueueRef = useRef<{ id: string; endTime: number }[]>([]);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(true);

  const [alertCount, setAlertCount] = useState<number>(0);

  useEffect(() => {
    if (location.state) {
      const { resumeContent: content, instructions: inst } = location.state as {
        resumeContent?: string;
        instructions?: string;
      };

      if (content) {
        setResumeContent(content);
        setInstructions(inst || '');
        return; // Exit if content is found
      }
    }

    // Redirect to upload page if no resume content is found
  }, [location.state, navigate]);

  const handleAlertCount = (count: number) => {
    setAlertCount(count);
    console.log(count);
  };

  const [sessionId] = useState(crypto.randomUUID());

  const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY || '';

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );

  const clientRef = useRef<RealtimeClient>(
    new RealtimeClient({
      apiKey: OPENAI_API_KEY,
      dangerouslyAllowAPIKeyInBrowser: true,
    })
  );

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);

  const [isConnected, setIsConnected] = useState(false);
  const [memoryKv, setMemoryKv] = useState<{ [key: string]: any }>({});

  // Add this near other state declarations
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const INACTIVITY_TIMEOUT = 60000; // 30 seconds of inactivity before prompting

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */

  
  const connectConversation = useCallback(async () => {
    if (isConnected) return;

    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    startTimeRef.current = new Date().toISOString();

    try {
      const [cameraPermission, micPermission] = await Promise.all([
        navigator.mediaDevices.getUserMedia({ video: true }),
        navigator.mediaDevices.getUserMedia({ audio: true }),
      ]);

      cameraPermission.getTracks().forEach((track) => track.stop());
      micPermission.getTracks().forEach((track) => track.stop());

      await wavRecorder.begin();
      await wavStreamPlayer.connect();
      await client.connect();

      // Set up the session with instructions
      await client.updateSession({
        instructions: instructions,
        input_audio_transcription: { model: 'whisper-1' },
        turn_detection: { type: 'server_vad' }, // Always use VAD mode
      });

      // Initialize recording immediately
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));

      if (resumeContent) {
        await client.sendUserMessageContent([
            {
                type: 'input_text',
                text: `
    System settings:
    Tool use: enabled.
    
    Resume content: 
    ${resumeContent}
    
    Instructions:
    - You are an **AI interviewer** responsible for conducting a **real-time, structured interview session** based on the provided resume content.
    - Use the candidate’s name (Record User in this case) to personalize the start of the interview. Example: "Let's begin the interview, Record User."
    - Do not ask any questions about the candidate's **name or email** during the interview.
    - After the introduction, immediately proceed to the **Education section**, followed by Work Experience, Projects, Certifications, and Skills — ensuring every individual entry in each section is covered one by one.
    
    Mandatory Coverage Rule:
    - You must cover **each section** and **each individual item within every section (work experience, projects, certifications, skills)**.
    - No combining or skipping entries — **each work experience, project, certification, and skill must be discussed individually in the exact order they appear in the resume**.
    - Example for projects: First discuss "Web Project on Online Grievance Redressal System," then "Final Year Project On Digital Sketch To Photo Matching Algorithm," then "demo" — ensuring all are covered.
    
    Section Order & Coverage Process:
    1. Education
    2. Work Experience (each job individually)
    3. Projects (each project individually)
    4. Certifications (each certification individually)
    5. Skills (each skill individually)
    
    For each entry (job, project, certification, skill), follow this question flow:
    - Start with **basic context questions** (e.g., "Can you describe your role as a Software Engineer?")
    - Ask **at least one follow-up question** after every response.
    - Follow-ups should explore:
        - Reasoning behind decisions and actions taken.
        - Real-life challenges faced and how they were handled.
        - Tools, technologies, and methods chosen — and why.
        - Key lessons learned and what they would do differently now.
        - How the experience applies to future work.
    - Ensure follow-ups are **dynamic and adaptive** based on the candidate's responses.
    
    Specific Section Guidance:
    
    Education Section:
    - Ask why they chose this field.
    - What subjects they found most challenging or rewarding.
    - How their academic work has helped in practical work.
    - Example: "You studied Computer Science — which programming concepts do you use most frequently in your work today?"
    
    Work Experience Section:
    - For each role (e.g., Software Engineer, IT Analyst, etc.), ask:
        - What were your primary responsibilities?
        - How did your role evolve over time?
        - What tools, technologies, and frameworks were used, and why?
        - What major challenges did you face, and how were they resolved?
        - What impact did your work have on the business, team, or product?
    - Example: "In your IT Analyst role, how did you identify opportunities to reduce web designer time by 95%?"
    
    Projects Section:
    - For each project (including placeholder or demo projects), ask:
        - What was the goal of the project?
        - What was your personal role and contribution?
        - What technologies or methodologies were used, and why?
        - What challenges did you face during the project?
        - If you were to redo this project, what would you change?
    - Example: "In the Final Year Project on Digital Sketch to Photo Matching, how did you design the matching algorithm and why?"
    
    Certifications Section:
    - For each certification, ask:
        - Why did you pursue this certification?
        - What were the key skills or knowledge gained?
        - Which parts were most difficult, and how did you overcome them?
        - How has this certification helped you in real-world work?
        - Would you recommend this certification to others, and why?
    - Example: "How has the demo 1 certification helped you in your recent projects?"
    
    Skills Section:
    - For each skill (Communication Skills, Tableau, B2B Sales), ask:
        - How did you develop this skill?
        - Can you give a real-life example of using this skill at work?
        - How do you keep this skill updated?
        - How would you apply this skill to a new challenge in your next role?
    - Example: "You listed Tableau — can you walk me through a dashboard you built and what challenges you faced when visualizing data?"
    
    Handling Missing Sections:
    - If any section (Education, Projects, Certifications, etc.) is missing, acknowledge it briefly and ask:
        - "Since this section is blank, could you share any relevant experiences or information related to this area?"
    
    Dynamic Follow-Ups (Mandatory):
    - Every response must trigger at least **one follow-up question**, exploring:
        - Decisions made.
        - Thought process.
        - Problem-solving techniques.
        - Tools, methods, and approaches used.
        - Collaboration, leadership, and soft skills where relevant.
    
    Engagement Duration:
    - The interview must last a **minimum of 15 minutes**, even if all sections are covered before that.
    - If all resume sections are exhausted, smoothly transition to **role-based and career-oriented general questions**, such as:
        - How do you keep your technical skills updated?
        - What’s a recent technical challenge you solved outside of work?
        - What type of work culture or environment do you thrive in?
    
    Hints & Guidance:
    - If the candidate struggles, provide **a maximum of two hints**, never direct answers.
    - Example: "You might want to mention how you approached error handling in your page generation tool."
    
    Language Requirement:
    - Politely require the candidate to respond in **English only**, with a gentle reminder if needed.
    
    Question Flow Control:
    - Strictly follow **forward-only flow** — once a question is asked, do not allow revisiting or re-answering previous questions.
    - Politely decline any request to return to earlier questions.
    
    Tone & Professionalism:
    - Maintain a **professional, structured, and evaluative tone**.
    - Avoid off-topic conversation.
    - Be polite but firm when enforcing:
        - Forward flow.
        - English-only rule.
        - Full section coverage.
    
    Complexity Level (Medium):
    - Target medium complexity, balancing:
        - Basic factual recall.
        - Thought process and reasoning.
        - Process awareness (how they approached tasks).
        - Applied knowledge (how they would improve the work today).
    - Example: "When working with high-volume data ingestion, how did you ensure the system could handle sudden spikes?"
    
    Overall Experience:
    - Ensure the interview feels like a **real-world professional evaluation**, not just a scripted Q&A.
    - Make sure the candidate feels they are being assessed on:
        - Critical thinking.
        - Technical and process understanding.
        - Communication skills.
        - Real-world application of knowledge.
    - Maintain **continuous flow with logical transitions**, ensuring all sections and entries are covered comprehensively.
    
                `,
            },
        ]);
    }
    

      setIsConnected(true);
      setRealtimeEvents([]);
      setItems(client.conversation.getItems());
    } catch (error) {
      console.error('Error connecting:', error);
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          alert(
            'Please allow access to both camera and microphone to start the interview.'
          );
        } else if (error.name === 'NotFoundError') {
          alert(
            'Camera or microphone not found. Please ensure your devices are properly connected.'
          );
        } else {
          alert(
            'Error accessing camera or microphone. Please check your device settings.'
          );
        }
      }
      await disconnectConversation();
    }
  }, [isConnected, instructions, resumeContent]);

  
  // useEffect(() => {
  //   if (eventsScrollRef.current) {
  //     const eventsEl = eventsScrollRef.current;
  //     const scrollHeight = eventsEl.scrollHeight;
  //     // Only scroll if height has just changed
  //     if (scrollHeight !== eventsScrollHeightRef.current) {
  //       eventsEl.scrollTop = scrollHeight;
  //       eventsScrollHeightRef.current = scrollHeight;
  //     }
  //   }
  // }, [realtimeEvents]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  // useEffect(() => {
  //   let isLoaded = true;

  //   const wavRecorder = wavRecorderRef.current;
  //   const clientCanvas = clientCanvasRef.current;
  //   let clientCtx: CanvasRenderingContext2D | null = null;

  //   const wavStreamPlayer = wavStreamPlayerRef.current;
  //   const serverCanvas = serverCanvasRef.current;
  //   let serverCtx: CanvasRenderingContext2D | null = null;

  //   const render = () => {
  //     if (isLoaded) {
  //       if (clientCanvas) {
  //         if (!clientCanvas.width || !clientCanvas.height) {
  //           clientCanvas.width = clientCanvas.offsetWidth;
  //           clientCanvas.height = clientCanvas.offsetHeight;
  //         }
  //         clientCtx = clientCtx || clientCanvas.getContext('2d');
  //         if (clientCtx) {
  //           const result = wavRecorder.recording
  //             ? wavRecorder.getFrequencies('voice')
  //             : { values: new Float32Array([0]) };
  //           WavRenderer.drawWave(
  //             clientCanvas,
  //             clientCtx,
  //             result.values,
  //             '#0099ff',
  //             4, // ChatGPT style uses 4 dots
  //             25, // Larger base radius for more prominent dots
  //             true // Keep smooth rendering
  //           );
  //         }
  //       }
  //       if (serverCanvas) {
  //         if (!serverCanvas.width || !serverCanvas.height) {
  //           serverCanvas.width = serverCanvas.offsetWidth;
  //           serverCanvas.height = serverCanvas.offsetHeight;
  //         }
  //         serverCtx = serverCtx || serverCanvas.getContext('2d');
  //         if (serverCtx) {
  //           const result = wavStreamPlayer.analyser
  //             ? wavStreamPlayer.getFrequencies('voice')
  //             : { values: new Float32Array([0]) };
  //           WavRenderer.drawWave(
  //             serverCanvas,
  //             serverCtx,
  //             result.values,
  //             '#eb7c49',
  //             4, // ChatGPT style uses 4 dots
  //             25, // Larger base radius
  //             true // Keep smooth rendering
  //           );
  //         }
  //       }
  //       window.requestAnimationFrame(render);
  //     }
  //   };
  //   render();

  //   return () => {
  //     isLoaded = false;
  //   };
  // }, []);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   * realtime conversation between openai and user
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ instructions: instructions });
    // Set transcription, otherwise we don't get user transcriptions back
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    // handle realtime events from client + server for event logging
    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    client.on('error', (event: any) => console.error(event));
    client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });

    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        setIsProcessingAudio(true);
        setIsAISpeaking(true);

        // Calculate audio duration
        const wordCount = item.formatted?.transcript?.split(' ').length || 0;
        const estimatedDuration = Math.max((wordCount / 150) * 60 * 1000, 2000); // minimum 2 seconds

        // Add to queue
        const endTime = Date.now() + estimatedDuration;
        audioQueueRef.current.push({ id: item.id, endTime });

        wavStreamPlayer.add16BitPCM(delta.audio, item.id);

        // Clear any existing timeout
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }

        // Set new timeout for this chunk
        processingTimeoutRef.current = setTimeout(() => {
          // Remove expired items from queue
          const now = Date.now();
          audioQueueRef.current = audioQueueRef.current.filter(
            (item) => item.endTime > now
          );

          // If queue is empty, stop speaking
          if (audioQueueRef.current.length === 0) {
            setIsAISpeaking(false);
            setIsProcessingAudio(false);
          }
        }, estimatedDuration + 500); // Add small buffer
      }

      // Store transcript when completed
      if (item.status === 'completed') {
        if (item.formatted.audio?.length) {
          const wavFile = await WavRecorder.decode(
            item.formatted.audio,
            24000,
            24000
          );
          item.formatted.file = wavFile;
        }

        // Store transcript if available
        if (item.formatted?.transcript) {
          try {
            await fetch('https://rfs-ai-interview-api-6xf7.onrender.com/api/transcripts', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                sessionId,
                role: item.role,
                transcript: item.formatted.transcript,
              }),
            });
          } catch (error) {
            console.error('Error storing transcript:', error);
          }
        }
      }
      setItems(items);
    });

    setItems(client.conversation.getItems());

    return () => {
      // cleanup; resets to defaults
      client.reset();
    };
  }, []);

  //These is for timer start to

  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (isConnected) {
      timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }

    if (timeLeft === 0 && isConnected) {
      setIsConnected(false); // Disconnect when timer hits 0
    }

    return () => {
      if (timer) {
        clearInterval(timer); // Cleanup interval
      }
    };
  }, [isConnected, timeLeft]);

  const getMinutes: number = Math.floor(timeLeft / 60);
  const getSeconds: number = timeLeft % 60;

  const disconnectConversation = useCallback(async () => {
    try {
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      // Capture the total duration directly before disconnecting
      const calculatedDuration = 900 - timeLeft; // Ensure you're using the latest timeLeft

      // Optional: Store totalDuration or make API request to save it
      await fetch('https://rfs-ai-interview-api-6xf7.onrender.com/api/transcripts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          totalDuration: calculatedDuration, // Send the calculated total duration
        }),
      });

      console.log('Executed API request successfully');

      // Disconnect and stop everything
      client.disconnect();
      await wavRecorder.end();
      await wavStreamPlayer.interrupt();

      // Clear session and other relevant state
      setIsConnected(false);
      setRealtimeEvents([]);
      setItems([]);
      setMemoryKv({});

      // Navigate to the thank you page
      navigate(`/feedback/${sessionId}`, {
        replace: true,
        state: { sessionId },
      });
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  }, [navigate, timeLeft]); // Make sure to include timeLeft in the dependencies

  useEffect(() => {
    if (timeLeft === 0 && isConnected) {
      disconnectConversation(); // Trigger disconnection when timer ends
      return; // Stop further execution
    }
  }, [timeLeft, isConnected, disconnectConversation]);
  const [speakingRole, setSpeakingRole] = useState<'assistant' | 'user' | null>(
    null
  );

  /**
   * Update the progress bar based on time left
   * This effect will run every time timeLeft changes
   * It will update the progress bar based on the time left
   */
  useEffect(() => {
    const canvas = document.getElementById(
      'progressCanvas'
    ) as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const radius = 11; // Radius of the circle
    const lineWidth = 5; // Width of the progress bar
    const totalSeconds = 300; // Total time (5 minutes)

    // Calculate progress percentage
    const percentage = timeLeft / totalSeconds;

    // Determine color based on percentage
    let color = '#52c41a'; // Green
    if (percentage <= 0.67 && percentage > 0.34) color = '#faad14'; // Yellow
    if (percentage <= 0.34) color = '#ff4d4f'; // Red

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background circle
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#e0e0e0'; // Gray background
    ctx.lineWidth = lineWidth;
    ctx.stroke();

    // Draw progress arc
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      radius,
      -Math.PI / 2, // Start at top
      -Math.PI / 2 + Math.PI * 2 * percentage, // End based on percentage
      false
    );
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [timeLeft]);

  const [showendmessage, setShowendmessage] = useState(false);
  const handleStopClick = () => {
    setShowendmessage((prev) => !prev);
  };

  interface MediaDevice {
    deviceId: string;
    label: string;
  }

  const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([]);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDevice[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDevice[]>(
    []
  );

  // Selected devices
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] =
    useState<string>('');
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] =
    useState<string>('');

  // Stream and video reference
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  // Initialize devices
  const initializeDevices = async () => {
    try {
      // Request device permissions
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });

      // Enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Categorize devices
      const videoInputs = devices
        .filter((device) => device.kind === 'videoinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 5)}`,
        }));

      const audioInputs = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
        }));

      const audioOutputs = devices
        .filter((device) => device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 5)}`,
        }));

      // Update state
      setVideoDevices(videoInputs);
      setAudioInputDevices(audioInputs);
      setAudioOutputDevices(audioOutputs);

      // Auto-select first devices if available
      if (videoInputs.length) setSelectedVideoDevice(videoInputs[0].deviceId);
      if (audioInputs.length)
        setSelectedAudioInputDevice(audioInputs[0].deviceId);
      if (audioOutputs.length)
        setSelectedAudioOutputDevice(audioOutputs[0].deviceId);
    } catch (error) {
      console.error('Device initialization error:', error);
    }
  };

  // Start media stream
  const startMediaStream = async () => {
    try {
      // Stop existing stream
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }

      // Create new stream
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: selectedVideoDevice },
        audio: { deviceId: selectedAudioInputDevice },
      });

      setMediaStream(newStream);

      // Attach stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (error) {
      console.error('Stream start error:', error);
    }
  };

  // Change device
  const changeDevice = async (
    deviceType: 'video' | 'audio',
    deviceId: string
  ) => {
    if (deviceType === 'video') setSelectedVideoDevice(deviceId);
    if (deviceType === 'audio') setSelectedAudioInputDevice(deviceId);
    await startMediaStream();
  };

  // Cleanup on unmount
  useEffect(() => {
    initializeDevices();
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Start stream when devices are selected
  useEffect(() => {
    if (selectedVideoDevice && selectedAudioInputDevice) {
      startMediaStream();
    }
  }, [selectedVideoDevice, selectedAudioInputDevice]);

  const [isChecking, setIsChecking] = useState(false);
  const handleOpenThis = () => {
    console.log('I was clicked');
    setIsChecking((prev) => {
      console.log('New state:', !prev);
      return !prev;
    });
  };

  //audio render

  useEffect(() => {
    const audio = new Audio('/testingspeaker.mp3'); // Ensure the path is correct

    const timer = setTimeout(() => {
      setShowPopup(true);
      audio
        .play()
        .catch((error) => console.error('Audio playback error:', error));
    }, 1000);

    return () => {
      clearTimeout(timer);
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (items.length) {
      const lastItem = items[items.length - 1];
      setSpeakingRole(
        lastItem.role === 'assistant' || lastItem.role === 'user'
          ? lastItem.role
          : null
      );
    }
  }, [items]);

  const [showpopup, setShowPopup] = useState(true);

  const handlestartinterview = () => {
    connectConversation();
    setShowPopup((prev) => !prev);
  };

  let currentAudio: HTMLAudioElement | null = null;
  const testSpeaker = async (deviceId: string) => {
    if (!deviceId) return;

    try {
      // Stop and reset the previously playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      // Create a new audio instance
      const audio = new Audio('/testingspeaker.mp3'); // Ensure this file exists
      audio.volume = 0.8;

      if ('setSinkId' in audio) {
        await (audio as any).setSinkId(deviceId);
        console.log(`Output device set to: ${deviceId}`);
      } else {
        console.warn('setSinkId() is not supported in this browser.');
      }

      currentAudio = audio; // Store the current audio instance
      await audio.play(); // Play test sound
    } catch (error) {
      console.error('Error playing test sound:', error);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      message.warning('Feedback cannot be empty.');
      return;
    }

    try {
      const response = await fetch('https://rfs-ai-interview-api-6xf7.onrender.com/api/user-query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId, // Sending the generated sessionId
          content: feedback, // Sending user-typed feedback
        }),
      });

      if (response.ok) {
        message.success('Feedback submitted successfully!');
        setFeedback(''); // Clear feedback after submission
        setIsModalOpen(false); // Close modal after successful submission
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      message.error('Error submitting feedback. Please try again.');
    }
  };

  return (
    <div className="h-screen bg-[#232323] text-white  ">
      {/* Initial mic check popup */}
      <Modal
        open={showpopup}
        footer={null}
        centered
        closable={false}
        className="rounded-2xl shadow-lg"
      >
        <div className="flex flex-col justify-center items-center p-6">
          <h2 className="text-lg font-semibold text-gray-900 text-center">
            Quick Speaker Check
          </h2>

          <p className="text-sm text-gray-500 text-center mt-2">
            Record AI is speaking...
          </p>

          <div className="w-full mt-3 h-1.5 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute left-0 h-1.5 bg-blue-500 rounded-full w-1/3 animate-bounce-progress"></div>
          </div>

          <div className="flex w-full mt-6 space-x-4">
            <button
              onClick={handlestartinterview}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg w-full"
            >
              {' '}
              Yes, I can hear you
            </button>

            <Link to="/start" className="w-full">
              <button className="bg-gray-200 text-gray-600 text-sm font-medium py-2 px-4 rounded-lg w-full">
                Try Rejoin
              </button>
            </Link>
          </div>
        </div>
      </Modal>

      <div className="Header">
        <div className="webcam-wrapper">
          <WebCam
            onAlertCountReached={handleAlertCount}
            speakingRole={speakingRole}
          />
        </div>
      </div>
      <div className="main ">
        <div className=" flex flex-col text-center justify-center items-center">
          <div className=" flex justify-center items-center space-x-1">
            <div>
              <img className="w-[10.21px]" src="/vector.png"></img>
            </div>
            <p className="leading-[10.93px] no-underline  text-[#FFFFFF] text-md ">
              Question Asked
            </p>
          </div>
          <div
            className="w-[823px] h-[102px] mt-2 flex items-center justify-center px-4 text-center"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: '400' }}
          >
            {/* Filter to only show assistant items */}
            {items
              .filter(
                (conversationItem) => conversationItem.role === 'assistant'
              ) // Show only assistant
              .slice(-1) // Show only the latest message from assistant
              .map((conversationItem) => (
                <div
                  key={conversationItem.id}
                  className="max-w-full text-justify leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <p className="text-sm">
                    {conversationItem.formatted.transcript ||
                      conversationItem.formatted.text ||
                      'Listening...'}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="Footer">
        <div className="w-full absolute bottom-0 left-0  ">
          <div className=" flex justify-between p-8 items-center">
            <div className=" flex justify-center items-center">
              <p className=" font-medium">Self Assessment</p>
              <div className="flex space-x-3 p-4 rounded-lg">
                {/* showing the microphone and speaker details */}

                {isChecking && (
                  <div className="mx-auto w-auto h-auto absolute bottom-20 left-48 p-6 bg-white rounded-lg shadow-lg space-y-6">
                    <div className=" flex justify-between">
                      <h2 className="text-xl font-semibold text-gray-800">
                        Settings
                      </h2>
                      <X
                        style={{ color: '#8F8F8F', cursor: 'pointer' }}
                        onClick={handleOpenThis}
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blueai mb-1">
                          Speaker
                        </label>
                        <div className="relative z-10 flex items-center">
                          <img
                            src="./audio.png"
                            alt="Icon"
                            className="absolute left-3"
                            style={{ width: '30px', height: '30px' }}
                          />
                          <select
                            style={{ color: '#000000' }}
                            className="w-full pl-11 pr-4 py-2 border rounded-lg"
                            onChange={(e) => testSpeaker(e.target.value)}
                          >
                            {audioOutputDevices.length > 0 &&
                              audioOutputDevices.map((device) => (
                                <option
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label || 'Unknown Device'}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blueai mb-1">
                          Microphone
                        </label>
                        <div className="relative z-10 flex items-center">
                          <img
                            src="./mic.png"
                            alt="Icon"
                            className="absolute left-3"
                            style={{ width: '30px', height: '30px' }}
                          />

                          <select
                            style={{ color: '#000000' }}
                            className="w-full pl-11 pr-4 py-2 border rounded-lg"
                          >
                            {audioInputDevices.length > 0 &&
                              audioInputDevices.map((device) => (
                                <option
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label || 'Unknown Device'}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>

                      {/* Video */}
                      <div>
                        <label className="block text-sm font-medium text-blueai mb-1">
                          Video
                        </label>
                        <div className="relative z-10 flex items-center">
                          <img
                            src="./camera.png"
                            alt="Icon"
                            className="absolute left-3"
                            style={{ width: '30px', height: '30px' }}
                          />

                          <select
                            style={{ color: '#000000' }}
                            className="w-full pl-11 pr-4 py-2 border rounded-lg"
                          >
                            {' '}
                            {videoDevices.length > 0 &&
                              videoDevices.map((device) => (
                                <option
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label || 'Unknown Device'}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Settings Icon */}
                <button
                  className="w-10 h-10 flex items-center  justify-center rounded-full cursor-pointer z-10 focus:outline-none"
                  onClick={handleOpenThis}
                >
                  <Settings className="w-6 h-6 text-white " />
                </button>

                {/* Warning Icon */}

                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer z-10"
                  onClick={() => setIsModalOpen(true)}
                >
                  <AlertOctagon className="w-6 h-6 text-white" />
                </button>

                {/* Feedback Modal user can write the query if he faced any issuses */}
                <Modal
                  open={isModalOpen}
                  footer={null}
                  centered
                  closable={false}
                  className="rounded-2xl shadow-lg"
                  onCancel={() => setIsModalOpen(false)}
                >
                  <div className="flex flex-col justify-center items-center p-6 w-full max-w-md">
                    <h2 className="text-lg font-semibold text-gray-900 text-center">
                      Feedback
                    </h2>
                    <textarea
                      className="w-full mt-4 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your feedback here..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />
                    <button
                      className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg w-full"
                      onClick={handleSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </Modal>
              </div>

              {/* End message popup */}

              <Modal
                open={showendmessage}
                footer={null}
                centered
                closable={false}
                className="rounded-2xl shadow-lg"
              >
                <div className="flex flex-col justify-center items-center p-8">
                  <h2 className="text-lg font-bold text-gray-900 text-center">
                    Are you sure you want to end?
                  </h2>

                  <p className="text-sm text-gray-500 text-center mt-2">
                    You cannot resume if you end this call. <br /> But you can
                    retake anytime.
                  </p>

                  <div className="flex justify-center items-center mt-6 space-x-4">
                    <button
                      onClick={disconnectConversation}
                      className="bg-blue-600 hover:bg-blue-700 cursor-pointer text-white text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      Yes, I’m sure
                    </button>

                    <button
                      onClick={handleStopClick}
                      className="bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-700 text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
            <div className="flex justify-center items-center space-x-4 ">
              <img src="/frame3.png"></img>
              <p className="text-md ">RecordAI</p>
              <img
                src="/wave.png"
                className={`absolute bottom-0 opacity-100  `}
                alt="Wave visualization"
              />
            </div>
            <div className="flex justify-center items-center p-4 space-x-2 text-white">
              <div className="flex items-center space-x-2">
                <p className=" font-medium"> Remaining Time |</p>
                <p className="font-mono">
                  {`${getMinutes.toString().padStart(2, '0')}:${getSeconds
                    .toString()
                    .padStart(2, '0')}`}
                </p>
                <canvas
                  id="progressCanvas"
                  width="35"
                  height="35"
                  className="rounded-lg shadow-md"
                ></canvas>
                <div>
                  <button
                    className="flex items-center justify-center w-20 h-10 bg-red-600 rounded-full hover:bg-red-700 transition duration-300 "
                    onClick={handleStopClick}
                  >
                    <img src="/phone-icon.png " alt="End Button" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
